import { PhoneOutlined } from "@mui/icons-material";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Divider, FormGroup, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useContent } from "../../logic";
import { useGetOrder } from "../../logic/useApiQuery";
import { isAdhocPortalOrder } from "../../utils/isAdhocPortalOrder";
import { SmsPreferenceStatusDialog } from "./SmsPreferenceStatusDialog";
import { SmsRevokeConsentDialog } from "./dialogs/SmsRevokeConsentDialog";
import { useOrganization } from "../OrganizationProvider";

type NotificationPreferenceView = 'notification' | 'sms'

export function NotificationPreferenceStatusDialog({ open, setOpen }: {
  open: boolean;
  setOpen: Function;
}) {
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const [activeView, setActiveView] = useState<NotificationPreferenceView>('notification')
  const [confirmingRevoke, setConfirmingRevoke] = useState(false)
  const { CompanyDisplayName } = useOrganization()
  const { ClosingText } = useContent()

  const initialPhoneNumber = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.phoneNumber) || ''
  const initialConsent = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.consent) ?? false
  const hasExistingSmsConsent = initialConsent && !!initialPhoneNumber
  const matchingPhoneNumber = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.matchingPhoneNumber)
  const MISMATCH_PHONE_ERROR_TEXT = <><strong>Phone number not eligible.</strong><br />
  The phone number provided is not currently eligible for SMS updates at this time. <br />
  Please confirm this is the phone number <strong>{CompanyDisplayName}</strong> has on file for this {ClosingText}.</>
  const SIGNED_UP_PHONE_TEXT = 'You have consented to receive transaction updates at:'
  const NOT_SIGNED_UP_PHONE_TEXT = 'You have not yet provided consent to receive SMS updates about your transaction.'

  useEffect(() => {
    if (open) {
      setActiveView('notification')
      setConfirmingRevoke(false)
    }
  }, [open])

  if (!globalOrderData) { return null }
  if (isAdhocPortalOrder(globalOrderData.Order)) { return null }

  if (activeView === 'sms') {
    return (
      <SmsPreferenceStatusDialog
        open={open}
        setOpen={setOpen}
        back={() => setActiveView('notification')}
      />
    )
  }

  if (confirmingRevoke) {
    return (
      <SmsRevokeConsentDialog
        open={open}
        back={() => { setConfirmingRevoke(false) }}
      />
    )
  }

  return (
    <Dialog
      fullWidth={true}
      key="userCancel"
      open={open}
      onClose={() => setOpen(false)}
    >
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }} variant='h6'>
          Notification Settings
        </DialogContentText>
        <FormGroup>
          { hasExistingSmsConsent &&
            <Box>
              { !matchingPhoneNumber && <Alert icon={<CancelOutlinedIcon />} severity="error">
                { MISMATCH_PHONE_ERROR_TEXT }
              </Alert> }
              { matchingPhoneNumber && <Alert severity="success">
                { SIGNED_UP_PHONE_TEXT }
              </Alert> }
              <Box sx={{ mt: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { sm: 'center' } }}>
                <Box display={'flex'}flexDirection='row' alignItems='center' gap={1}>
                  <PhoneOutlined color="action" />
                  <Box>
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {initialPhoneNumber}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto'}, ml: { sm: 'auto' } }} >
                  <Button
                    sx = {{ flex: { xs: 1, sm: 'none' } }}
                    type="button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setActiveView('sms')}
                  >
                    Edit
                  </Button>
                  <Button
                    sx = {{ flex: { xs: 1, sm: 'none' } }}
                    type="button"
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => setConfirmingRevoke(true)}
                  >
                    Revoke Consent
                  </Button>
                </Box>
              </Box>
            </Box>
          }
          {!hasExistingSmsConsent &&
            <>
              <Alert severity="warning">
                { NOT_SIGNED_UP_PHONE_TEXT }
              </Alert>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { sm: 'center' } }}>
                <Box display={'flex'} flexDirection='row' alignItems='center' gap={1}>
                  <PhoneOutlined color="action" />
                  <Box>
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      Not consented 
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto'}, ml: { sm: 'auto' } }} >
                  <Button
                    sx = {{ flex: { xs: 1, sm: 'none' } }}
                    type="button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setActiveView('sms')}
                  >
                    Consent to text messages 
                  </Button>
                </Box>
              </Box>
            </>
          }
        </FormGroup>
      </DialogContent>
      <Divider/>
      <DialogActions sx={{ p: 2 }}>
        <Button type="button" variant="contained" onClick={() => setOpen(false)} sx={{ width: {xs: '100%', sm: 'auto' }}}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
